import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { DataGridColumn } from '../DataGrid.types';

interface ColumnToggleProps<T extends Record<string, unknown>> {
  columns: DataGridColumn<T>[];
  hiddenColumnIds: string[];
  onToggleColumn: (columnId: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
}

interface DropdownPosition {
  top: number;
  left: number;
  transformOrigin: string;
}

// Icons
const ColumnsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="2" y="2" width="4" height="12" rx="1" />
    <rect x="10" y="2" width="4" height="12" rx="1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 7l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ColumnToggleComponent<T extends Record<string, unknown>>({
  columns,
  hiddenColumnIds,
  onToggleColumn,
  onShowAll,
  onHideAll
}: ColumnToggleProps<T>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0, transformOrigin: 'top right' });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 240;
    const dropdownMaxHeight = 400;
    const padding = 8;

    let top = buttonRect.bottom + padding;
    let left = buttonRect.right - dropdownWidth;
    let transformOrigin = 'top right';

    // Check if dropdown would go off the right edge
    if (left < padding) {
      left = buttonRect.left;
      transformOrigin = 'top left';
    }

    // Check if dropdown would go off the bottom
    if (top + dropdownMaxHeight > window.innerHeight - padding) {
      // Position above the button
      top = buttonRect.top - dropdownMaxHeight - padding;
      transformOrigin = transformOrigin.replace('top', 'bottom');

      // If still doesn't fit, position at bottom of viewport
      if (top < padding) {
        top = padding;
        transformOrigin = 'top right';
      }
    }

    setPosition({ top, left, transformOrigin });
  }, []);

  // Update position when dropdown opens or window resizes
  useEffect(() => {
    if (isOpen) {
      calculatePosition();

      const handleResize = () => calculatePosition();
      const handleScroll = (event: Event) => {
        // Don't close if scrolling inside the dropdown itself
        if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) {
          return;
        }
        setIsOpen(false);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, calculatePosition]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        break;

      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = prev < columns.length - 1 ? prev + 1 : 0;
          return nextIndex;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = prev > 0 ? prev - 1 : columns.length - 1;
          return nextIndex;
        });
        break;

      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;

      case 'End':
        e.preventDefault();
        setFocusedIndex(columns.length - 1);
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < columns.length) {
          onToggleColumn(columns[focusedIndex].id);
        }
        break;

      case 'Tab':
        // Allow Tab to close the dropdown naturally
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  }, [isOpen, focusedIndex, columns, onToggleColumn]);

  // Focus management when dropdown opens
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex]);

  // Reset focus index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  const visibleCount = columns.length - hiddenColumnIds.length;
  const dropdownId = 'column-toggle-menu';

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      id={dropdownId}
      role="listbox"
      aria-label="Kolon seçimi"
      aria-multiselectable="true"
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: '240px',
        maxHeight: '400px',
        background: 'var(--color-bg-elevated)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border-default)',
        boxShadow: 'var(--shadow-xl)',
        zIndex: 9999,
        overflow: 'hidden',
        transformOrigin: position.transformOrigin,
        animation: 'scaleIn var(--transition-fast) forwards',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)'
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-3) var(--space-4)',
          borderBottom: '1px solid var(--color-border-subtle)',
          background: 'var(--color-bg-secondary)'
        }}
      >
        <span
          id="column-toggle-label"
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-text-tertiary)'
          }}
        >
          Kolon Görünürlüğü
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => {
              onShowAll();
              buttonRef.current?.focus();
            }}
            style={{
              padding: 'var(--space-1) var(--space-2)',
              fontSize: 'var(--text-xs)',
              fontWeight: 500,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'transparent',
              color: 'var(--color-primary-600)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-primary-50)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Tümü
          </button>
          <button
            onClick={() => {
              onHideAll();
              buttonRef.current?.focus();
            }}
            style={{
              padding: 'var(--space-1) var(--space-2)',
              fontSize: 'var(--text-xs)',
              fontWeight: 500,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-hover-overlay)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Hiçbiri
          </button>
        </div>
      </div>

      {/* Column List */}
      <div
        ref={listRef}
        style={{
          maxHeight: '320px',
          overflowY: 'auto',
          overscrollBehavior: 'contain'
        }}
      >
        {columns.map((column, index) => {
          const isVisible = !hiddenColumnIds.includes(column.id);
          const isFocused = focusedIndex === index;

          return (
            <button
              key={column.id}
              ref={el => { itemRefs.current[index] = el; }}
              role="option"
              aria-selected={isVisible}
              tabIndex={isFocused ? 0 : -1}
              onClick={() => onToggleColumn(column.id)}
              onFocus={() => setFocusedIndex(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                width: '100%',
                padding: 'var(--space-3) var(--space-4)',
                textAlign: 'left',
                fontSize: 'var(--text-sm)',
                border: 'none',
                background: isFocused ? 'var(--color-hover-overlay)' : 'transparent',
                cursor: 'pointer',
                color: isVisible ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
                transition: 'all var(--transition-fast)',
                outline: isFocused ? '2px solid var(--color-primary)' : 'none',
                outlineOffset: '-2px'
              }}
              onMouseEnter={(e) => {
                if (!isFocused) {
                  e.currentTarget.style.background = 'var(--color-hover-overlay)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isFocused) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  borderRadius: 'var(--radius-sm)',
                  border: isVisible
                    ? '2px solid var(--color-primary-500)'
                    : '2px solid var(--color-neutral-300)',
                  background: isVisible ? 'var(--color-primary-500)' : 'transparent',
                  color: 'white',
                  transition: 'all var(--transition-fast)',
                  flexShrink: 0
                }}
              >
                {isVisible && <CheckIcon />}
              </span>
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {column.header}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? dropdownId : undefined}
        aria-label={`Kolon görünürlüğü. ${visibleCount}/${columns.length} kolon görünür`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-sm)',
          fontWeight: 500,
          fontFamily: 'var(--font-display)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border-default)',
          background: isOpen ? 'var(--color-neutral-100)' : 'var(--color-bg-elevated)',
          color: 'var(--color-text-primary)',
          cursor: 'pointer',
          transition: 'all var(--transition-fast)'
        }}
      >
        <ColumnsIcon />
        <span>Kolonlar</span>
        <span
          aria-hidden="true"
          style={{
            padding: '0 var(--space-2)',
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-primary-100)',
            color: 'var(--color-primary-700)'
          }}
        >
          {visibleCount}/{columns.length}
        </span>
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}

export const ColumnToggle = memo(ColumnToggleComponent) as typeof ColumnToggleComponent;
